import { useEffect, useState } from 'react';
import { ApolloError, useMutation } from '@apollo/client';
import { message } from 'antd';

import { Button, Loading } from '../../../../components';
import { LabelAndButtonsRow } from '.';
import FetchError from '../../../../components/generic/FetchError';
import { formatLink } from '../../../../utils/formatLinks';
import NoAvailableData from '../../../../components/assets/NoAvailableData';
import BorderedSection from '../../../../components/generic/BorderedSection';
import AntModal from '../../../../components/generic/AntModal';
import AutomatedForm from '../../../../components/generic/Form/AutomatedForm';
import {
  CREATE_DOCUMENT_LINK,
  DELETE_DOCUMENT_LINK,
  UPDATE_DOCUMENT_LINK,
} from '../../queries/documentLink';
import { set } from 'lodash';
import StandaloneForm from '../../../../components/generic/Form/StandaloneForm';
import { ModifyVariablesArgs } from '../../../../components/generic/Table/TableV3';
import { useUserContext } from '../../../../context/PermissionsContext';

interface DocumentLinksProps {
  data?: any;
  error?: ApolloError;
  parentUuid: string | undefined;
  queryToUpdate: string;
  model: string;
}

const DocumentLinks = ({
  data,
  error,
  parentUuid,
  queryToUpdate,
  model,
}: DocumentLinksProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentDocumentIndex, setCurrentDocumentIndex] =
    useState<Object | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const { isAdmin, canEdit } = useUserContext();

  const currentDocumentLink = data?.find(
    (doc: any) => doc.uuid === currentDocumentIndex,
  );

  const fields = [
    {
      key: 'name',
      label: 'Document Name',
      type: 'text',
      isRequired: true,
    },
    {
      key: 'link',
      label: 'Link',
      type: 'link',
      isRequired: true,
    },
  ];

  const [CreateDocumentLink] = useMutation(CREATE_DOCUMENT_LINK, {
    refetchQueries: queryToUpdate ? [queryToUpdate] : [],
  });

  const [UpdateDocumentLink] = useMutation(UPDATE_DOCUMENT_LINK, {
    refetchQueries: queryToUpdate ? [queryToUpdate] : [],
  });

  const [DeleteDocumentLink] = useMutation(DELETE_DOCUMENT_LINK, {
    refetchQueries: queryToUpdate ? [queryToUpdate] : [],
  });

  const modifyVariables = ({ values }: ModifyVariablesArgs) => {
    if (currentDocumentLink) {
      return {
        ...values,
      };
    }

    return {
      name: values.name,
      link: values.link,
      [model]: { uuid: parentUuid },
    };
  };

  const handleEditRecord = (recordId: any) => {
    setIsModalOpen(true);
    setCurrentDocumentIndex(recordId);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentDocumentIndex(null);
  };

  const handleLoadingState = (loading: boolean) => {
    setIsLoading(loading);
  };

  useEffect(() => {
    if (!isModalOpen && currentDocumentLink) {
      setCurrentDocumentIndex(null);
    }
  }, [isModalOpen]);

  useEffect(() => {
    if (isLoading) setIsModalOpen(false);
  }, [isLoading]);

  return (
    <>
      {contextHolder}
      <BorderedSection title="Document Links">
        <section className="w-full max-h-[160px] pr-3.5 flex flex-col gap-6 overflow-y-auto">
          <DocumentList
            data={data}
            loading={isLoading}
            error={error}
            handleEditRecord={handleEditRecord}
          />
        </section>
        {(isAdmin || canEdit) && (
          <Button
            type="primary"
            className="w-[180px] mx-auto mt-5 text-sm"
            onClick={() => setIsModalOpen(true)}
          >
            + Add
          </Button>
        )}
      </BorderedSection>
      <AntModal
        openModal={isModalOpen}
        onCancel={handleCloseModal}
        title={
          currentDocumentLink ? 'Edit Document Links' : 'Add Document Links'
        }
      >
        <StandaloneForm
          fields={fields}
          createGQL={CreateDocumentLink}
          updateGQL={UpdateDocumentLink}
          deleteGQL={DeleteDocumentLink}
          modifyVariables={modifyVariables}
          currentRecord={currentDocumentLink}
          setIsLoading={handleLoadingState}
        />
      </AntModal>
    </>
  );
};

type DocumentListProps = {
  data: any;
  loading?: boolean;
  error?: ApolloError | undefined;
  handleEditRecord: (record: any) => void;
};

const DocumentList = ({
  data,
  loading,
  error,
  handleEditRecord,
}: DocumentListProps) => {
  if (!data || loading) return <Loading />;
  if (data.length === 0) return <NoAvailableData />;
  if (error) return <FetchError error={error.message} />;

  return data.map((doc: any) => (
    <section key={doc.uuid} className="flex flex-col gap-3">
      <LabelAndButtonsRow
        label={doc.name}
        onClickGo={() => window.open(formatLink(doc.link), '_blank')}
        onClickEdit={() => handleEditRecord(doc.uuid)}
      />
    </section>
  ));
};

export default DocumentLinks;
